// Feature flags for controlling application features
export const FEATURE_FLAGS = {
  PHONE_FEATURE_ENABLED: true, // Set to false to hide phone features throughout the app
  TRIAL_PLAN_ENABLED: true, // Set to false to disable trial functionality
  TRIAL_BILLING_FOR_ALL_ACCOUNTS: false, // Set to false to disable trial billing for all account types
  CONNECT_COMPANY_CALENDAR: true, // Set to false to hide Connect Company/Team Calendar section throughout the app
};

// Utility function to check if phone feature is enabled
export const isPhoneFeatureEnabled = () => FEATURE_FLAGS.PHONE_FEATURE_ENABLED;

// Utility function to check if trial feature is enabled
export const isTrialFeatureEnabled = () => FEATURE_FLAGS.TRIAL_PLAN_ENABLED;

// Utility function to check if all account types should get trial billing
export const isTrialBillingForAllAccountsEnabled = () => FEATURE_FLAGS.TRIAL_BILLING_FOR_ALL_ACCOUNTS;

// Utility function to check if company calendar connection feature is enabled
export const isConnectCompanyCalendarEnabled = () => FEATURE_FLAGS.CONNECT_COMPANY_CALENDAR;

// Account type to seat count mapping — reflects base_seats (seats included in plan price)
export const ACCOUNT_TYPE_SEATS = {
  trial: 1, Trial: 1,
  solo: 1, Solo: 1,
  solo_assistant: 2, Solo_Assistant: 2,
  small_team: 5, Small_Team: 5,
  medium_team: 10, Medium_Team: 10,
  large_team: 18, Large_Team: 18,
  small_brokerage: 22, Small_Brokerage: 22,
  medium_brokerage: 40, Medium_Brokerage: 40,
  large_brokerage: 80, Large_Brokerage: 80,
  enterprise: 150, Enterprise: 150,
  super: Infinity
};

// Base plan pricing (Founding Customer Pricing)
export const PRICING_LABEL = 'Founding Customer Pricing';

export const BASE_PLAN_PRICING = {
  trial: 0,
  solo: 49,
  solo_assistant: 89,
  small_team: 179,
  medium_team: 329,
  large_team: 579,
  small_brokerage: 699,
  medium_brokerage: 1199,
  large_brokerage: 2199,
  enterprise: null, // custom pricing
  super: 0
};

// Seat add-on pricing — $35/active seat/month above base_seats
export const SEAT_PRICING = {
  MONTHLY_COST_PER_SEAT: 35,
  BASE_PLAN_INCLUDED_SEATS: {
    trial: 1,
    solo: 1,
    solo_assistant: 2,
    small_team: 5,
    medium_team: 10,
    large_team: 18,
    small_brokerage: 22,
    medium_brokerage: 40,
    large_brokerage: 80,
    enterprise: null, // custom
    super: 0
  }
};

// Annual billing discount
export const ANNUAL_DISCOUNT = 0.20; // 20% discount for annual billing

// Maximum active seats allowed per plan before upgrade is required
export const MAX_SEATS = {
  trial: 1,
  solo: 1,
  solo_assistant: 3,
  small_team: 8,
  medium_team: 15,
  large_team: 25,
  small_brokerage: 40,
  medium_brokerage: 80,
  large_brokerage: 150,
  enterprise: null, // unlimited / custom
  super: 1000
};

// Phone slot add-on pricing — $65/slot/month, 500 min included per slot
export const PHONE_ADDON = {
  MONTHLY_COST_PER_SLOT: 65.00,
  INCLUDED_MINUTES: 500,
  OVERAGE_RATE_PER_MINUTE: 0.05,
  FEATURES: [
    'Hands-Free Schedule Management',
    'Closing-Smart Scheduling',
    'Seamless Multi-Party Coordination',
    'Proactive Closing Support',
    '24/7 Smart Assistant',
  ],
  // Phone slots included in the plan price (base entitlement, no purchase required)
  BASE_SLOTS_PER_ACCOUNT: {
    trial: 0, Trial: 0,
    solo: 0, Solo: 0,
    solo_assistant: 0, Solo_Assistant: 0, 'Solo Assistant': 0,
    small_team: 0, Small_Team: 0, 'Small Team': 0,
    medium_team: 0, Medium_Team: 0, 'Medium Team': 0,
    large_team: 0, Large_Team: 0, 'Large Team': 0,
    small_brokerage: 2, Small_Brokerage: 2, 'Small Brokerage': 2,
    medium_brokerage: 0, Medium_Brokerage: 0, 'Medium Brokerage': 0,
    large_brokerage: 0, Large_Brokerage: 0, 'Large Brokerage': 0,
    enterprise: null, Enterprise: null,
    super: 0
  },
  MAX_SLOTS_PER_ACCOUNT: {
    trial: 0,
    solo: 2,
    solo_assistant: 3,
    small_team: 8,
    medium_team: 15,
    large_team: 25,
    small_brokerage: 40,
    medium_brokerage: 80,
    large_brokerage: 150,
    enterprise: null, // custom
    super: 500
  },
  RECOMMENDED_SLOTS: {
    trial: 0,
    solo: 1,
    solo_assistant: 1,
    small_team: 3,
    medium_team: 5,
    large_team: 8,
    small_brokerage: 10,
    medium_brokerage: 20,
    large_brokerage: 40,
    enterprise: null,
    super: 20
  }
};

// Maximum admin users allowed per account type (admins do not consume team seats)
export const ADMIN_LIMITS = {
  trial: 0, Trial: 0,
  solo: 0, Solo: 0,
  solo_assistant: 1, Solo_Assistant: 1,
  small_team: 1, Small_Team: 1,
  medium_team: 2, Medium_Team: 2,
  large_team: 3, Large_Team: 3,
  small_brokerage: 5, Small_Brokerage: 5,
  medium_brokerage: 10, Medium_Brokerage: 10,
  large_brokerage: 20, Large_Brokerage: 20,
  enterprise: null, Enterprise: null, // custom
  super: Infinity, Super: Infinity,
};

// Trial Configuration
export const TRIAL_CONFIG = {
  DURATION_DAYS: 14,
  PLAN_DETAILS: {
    displayName: "14-Day Free Trial",
    description: "Experience all features with full access for 14 days",
    price: "$0",
    period: "14 days",
    features: [
      "Full access to all features",
      "1 seat included",
      "No payment required",
      "Cancel anytime"
    ]
  },
  REMINDER_SCHEDULE: {
    DAYS_BEFORE_EXPIRY: [7, 3, 1], // Days to send reminder emails
    GRACE_PERIOD_DAYS: 3 // Days after expiry before blocking access
  }
};
